import { useEffect, useRef, useState } from "react";
import {
  FaTimes,
  FaSearchPlus,
  FaSearchMinus,
  FaUndo,
  FaChevronLeft,
  FaChevronRight,
  FaLock,
  FaUnlock,
} from "react-icons/fa";
import type { HistoryItem } from "../../types/smileDesign/historyItem";

interface Props {
  beforeImage: string | null;
  history: HistoryItem[];
  selectedIndex: number;
  onImageChange: (item: HistoryItem) => void;
  onClose: () => void;
}

export default function FullscreenViewer({
  beforeImage,
  history,
  selectedIndex,
  onImageChange,
  onClose,
}: Props) {
  const [zoomBefore, setZoomBefore] = useState(1);
  const [zoomAfter, setZoomAfter] = useState(1);
  const [zoomLocked, setZoomLocked] = useState(false);
  const lastSideRef = useRef<"before" | "after">("before");

  const [currentIndex, setCurrentIndex] = useState(selectedIndex);

  useEffect(() => {
    setCurrentIndex(selectedIndex);
  }, [selectedIndex]);

  const currentImage = history[currentIndex];

  const [posBefore, setPosBefore] = useState({ x: 0, y: 0 });
  const [posAfter, setPosAfter] = useState({ x: 0, y: 0 });

  const [dragging, setDragging] = useState<null | "before" | "after">(null);
  const [start, setStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const clampZoom = (value: number) => Math.min(Math.max(value, 1), 5);

  const bumpZoom = (type: "before" | "after", delta: number) => {
    lastSideRef.current = type;
    const apply = (prev: number) => clampZoom(prev + delta);
    if (zoomLocked) {
      setZoomBefore(apply);
      setZoomAfter(apply);
      return;
    }
    if (type === "before") setZoomBefore(apply);
    else setZoomAfter(apply);
  };

  const handleWheel = (
    e: React.WheelEvent<HTMLDivElement>,
    type: "before" | "after"
  ) => {
    e.preventDefault();
    bumpZoom(type, e.deltaY < 0 ? 0.2 : -0.2);
  };

  const beginDrag = (clientX: number, clientY: number, type: "before" | "after") => {
    lastSideRef.current = type;
    setDragging(type);
    setStart({ x: clientX, y: clientY });
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragging) return;

    const dx = clientX - start.x;
    const dy = clientY - start.y;

    setStart({ x: clientX, y: clientY });
    const shift = (prev: { x: number; y: number }) => ({
      x: prev.x + dx,
      y: prev.y + dy,
    });

    if (zoomLocked) {
      setPosBefore(shift);
      setPosAfter(shift);
      return;
    }

    if (dragging === "before") setPosBefore(shift);
    else setPosAfter(shift);
  };

  const endDrag = () => {
    setDragging(null);
  };

  const resetView = (type: "before" | "after") => {
    lastSideRef.current = type;
    if (zoomLocked) {
      setZoomBefore(1);
      setZoomAfter(1);
      setPosBefore({ x: 0, y: 0 });
      setPosAfter({ x: 0, y: 0 });
      return;
    }
    if (type === "before") {
      setZoomBefore(1);
      setPosBefore({ x: 0, y: 0 });
    } else {
      setZoomAfter(1);
      setPosAfter({ x: 0, y: 0 });
    }
  };

  const toggleLock = () => {
    setZoomLocked((prev) => {
      if (!prev) {
        if (lastSideRef.current === "after") {
          setZoomBefore(zoomAfter);
          setPosBefore(posAfter);
        } else {
          setZoomAfter(zoomBefore);
          setPosAfter(posBefore);
        }
      }
      return !prev;
    });
  };

  const handlePrevious = () => {
    if (currentIndex === 0) return;

    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    onImageChange(history[newIndex]);

    if (zoomLocked) {
      setZoomAfter(zoomBefore);
      setPosAfter(posBefore);
    } else {
      setZoomAfter(1);
      setPosAfter({ x: 0, y: 0 });
    }
  };

  const handleNext = () => {
    if (currentIndex === history.length - 1) return;

    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    onImageChange(history[newIndex]);

    if (zoomLocked) {
      setZoomAfter(zoomBefore);
      setPosAfter(posBefore);
    } else {
      setZoomAfter(1);
      setPosAfter({ x: 0, y: 0 });
    }
  };

  const goPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlePrevious();
  };

  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleNext();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, history, onClose]);

  return (
    <div
      className="fixed inset-0 z-[999] bg-[#080808]/95 backdrop-blur-xl flex flex-col"
      onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchMove={(e) => {
        if (!dragging || e.touches.length === 0) return;
        moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      }}
      onTouchEnd={endDrag}
    >
      {/* Top Bar */}
      <div className="h-14 sm:h-20 border-b border-white/10 bg-[#101211]/90 px-3 sm:px-6 flex items-center justify-between shrink-0">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-2xl font-bold text-white truncate">Smile Preview</h2>
          <p className="text-xs sm:text-sm text-gray-400 mt-0.5 hidden sm:block">
            {zoomLocked
              ? "Zoom locked — scroll either side to zoom both"
              : "Scroll an image to zoom · lock to zoom both together"}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={toggleLock}
            title={
              zoomLocked
                ? "Unlock so each side zooms on its own"
                : "Lock zoom so both sides zoom together"
            }
            className={`h-10 sm:h-12 px-3 sm:px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-300 ${
              zoomLocked
                ? "bg-[#2fa9bf]/20 text-[#2fa9bf] border border-[#2fa9bf]/40"
                : "bg-white/5 text-white border border-white/10 hover:bg-[#2fa9bf] hover:border-[#2fa9bf]"
            }`}
          >
            {zoomLocked ? <FaLock /> : <FaUnlock />}
            <span className="hidden sm:inline">{zoomLocked ? "Locked" : "Lock"}</span>
          </button>
          <button
            onClick={onClose}
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white transition-all duration-300 flex items-center justify-center shrink-0"
            aria-label="Close fullscreen"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      {/* Images Container — stacked on mobile, side-by-side on md+ */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-y-auto md:overflow-hidden">

        {/* Before Column */}
        <div className="w-full md:w-1/2 md:border-r border-white/10 flex flex-col bg-[#0d0d0d] min-h-[45vh] md:min-h-0">
          <div className="h-12 sm:h-16 px-3 sm:px-4 border-b border-white/5 flex items-center justify-between shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-white">Before</h3>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => bumpZoom("before", 0.2)}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
              >
                <FaSearchPlus />
              </button>
              <button
                onClick={() => bumpZoom("before", -0.2)}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
              >
                <FaSearchMinus />
              </button>
              <button
                onClick={() => resetView("before")}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
              >
                <FaUndo />
              </button>
            </div>
          </div>

          <div
            className="flex-1 flex items-center justify-center overflow-hidden cursor-grab touch-none min-h-[220px]"
            onWheel={(e) => handleWheel(e, "before")}
            onMouseDown={(e) => beginDrag(e.clientX, e.clientY, "before")}
            onTouchStart={(e) => {
              if (e.touches.length === 0) return;
              beginDrag(e.touches[0].clientX, e.touches[0].clientY, "before");
            }}
          >
            {beforeImage && (
              <img
                src={beforeImage}
                alt="Before"
                draggable={false}
                className="object-contain max-w-full max-h-full select-none transition-transform duration-75"
                style={{
                  transform: `translate(${posBefore.x}px, ${posBefore.y}px) scale(${zoomBefore})`,
                  cursor: dragging === "before" ? "grabbing" : "grab",
                }}
              />
            )}
          </div>
        </div>

        {/* After Column */}
        <div className="w-full md:w-1/2 flex flex-col bg-[#101211] min-h-[45vh] md:min-h-0 border-t md:border-t-0 border-white/10">
          <div className="h-12 sm:h-16 px-3 sm:px-4 border-b border-white/5 flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-white shrink-0">After</h3>

              <p className="text-xs sm:text-sm text-gray-400 shrink-0">
                {currentIndex + 1} / {history.length}
              </p>

              {currentImage && (
                <p
                  className="text-xs sm:text-sm text-[#2fa9bf] truncate hidden sm:block"
                  title={currentImage.imageName}
                >
                  {currentImage.imageName}
                </p>
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <button
                onClick={() => bumpZoom("after", 0.2)}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
              >
                <FaSearchPlus />
              </button>
              <button
                onClick={() => bumpZoom("after", -0.2)}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
              >
                <FaSearchMinus />
              </button>
              <button
                onClick={() => resetView("after")}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
              >
                <FaUndo />
              </button>
            </div>
          </div>

          <div
            className="relative flex-1 flex items-center justify-center overflow-hidden cursor-grab touch-none min-h-[220px]"
            onWheel={(e) => handleWheel(e, "after")}
            onMouseDown={(e) => beginDrag(e.clientX, e.clientY, "after")}
            onTouchStart={(e) => {
              if (e.touches.length === 0) return;
              beginDrag(e.touches[0].clientX, e.touches[0].clientY, "after");
            }}
          >
            <button
              onClick={goPrevious}
              disabled={currentIndex === 0}
              className={`absolute left-2 sm:left-5 z-20 h-10 w-10 sm:h-14 sm:w-14 rounded-full flex items-center justify-center transition
                ${currentIndex === 0
                  ? "bg-white/5 text-gray-600 cursor-not-allowed opacity-30"
                  : "bg-black/60 hover:bg-[#2fa9bf] text-white backdrop-blur-sm cursor-pointer"
                }`}
            >
              <FaChevronLeft size={18} />
            </button>

            {currentImage && (
              <img
                src={currentImage.image}
                alt="After"
                draggable={false}
                className="object-contain max-w-full max-h-full select-none transition-transform duration-75"
                style={{
                  transform: `translate(${posAfter.x}px, ${posAfter.y}px) scale(${zoomAfter})`,
                  cursor: dragging === "after" ? "grabbing" : "grab",
                }}
              />
            )}

            <button
              onClick={goNext}
              disabled={currentIndex === history.length - 1}
              className={`absolute right-2 sm:right-5 z-20 h-10 w-10 sm:h-14 sm:w-14 rounded-full flex items-center justify-center transition
                ${currentIndex === history.length - 1
                  ? "bg-white/5 text-gray-600 cursor-not-allowed opacity-30"
                  : "bg-black/60 hover:bg-[#2fa9bf] text-white backdrop-blur-sm cursor-pointer"
                }`}
            >
              <FaChevronRight size={18} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
